/**
 * What a card can ask the person for. A step names a form and the context key the answer is
 * kept under; the app draws it and checks the answer.
 *
 * - `answers: 'text'`: a field on the card; the answer is the words, checked by the app (a
 *   creator handle becomes a profile link, say).
 * - `answers: 'node'`: the board's own ways to add something (upload, record, write, paste a
 *   link), offered on the card; the answer is the id of the first node of an accepted type
 *   the person adds while the card is up (the step's `accept`).
 *
 * A web test fails if a form listed here has no implementation in the app.
 */

export interface CoachFormDefinition {
  label: string;
  placeholder: string;
  answers: 'text' | 'node';
  /** A text box rather than a one-line field. */
  multiline?: boolean;
}

export const COACH_FORMS = {
  link: {
    label: 'A link',
    placeholder: 'Paste a link to a video, post, or website',
    answers: 'text',
  },
  'creator-profile': {
    label: 'A creator',
    placeholder: '@handle or a link to their Instagram or TikTok',
    answers: 'text',
  },
  text: { label: 'Some text', placeholder: 'Type or paste here', answers: 'text', multiline: true },
  content: {
    label: 'Something for the board',
    placeholder: 'Upload, record, or write it',
    answers: 'node',
  },
} as const satisfies Record<string, CoachFormDefinition>;

export type CoachFormId = keyof typeof COACH_FORMS;

export const COACH_FORM_IDS = Object.keys(COACH_FORMS) as CoachFormId[];

/**
 * Node types a `content` form can take as its answer: the board's own content. Social posts
 * Poppy reads (`facebookNode`, `linkedinNode`, `twitterNode`) land as `contentNode`.
 */
export const COACH_CONTENT_TYPES = [
  'audioNode',
  'textNode',
  'documentNode',
  'imageNode',
  'videoNode',
  'contentNode',
  'youtubeNode',
  'youtubeChannelNode',
  'tiktokNode',
  'instagramNode',
  'webScrapperNode',
  'facebookAdsNode',
  'loomNode',
  'facebookNode',
  'linkedinNode',
  'twitterNode',
] as const;

export type CoachContentType = (typeof COACH_CONTENT_TYPES)[number];

/** Forms whose answer is typed (the app checks each one's words). */
export type CoachTextFormId = {
  [K in CoachFormId]: (typeof COACH_FORMS)[K]['answers'] extends 'text' ? K : never;
}[CoachFormId];
